import { ColumnDef } from '@tanstack/react-table';
import ExcelJS from 'exceljs';
import moment from 'moment';

import { AgreementDTO } from '@/types/api-types';
import { NormalizedAgreement } from '@/types/file-explorer-types';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useGlobalStore } from '@/stores/globalStore';
import { useSearchStore } from '@/stores/searchStore';

import {
  COLUMN_NAMES,
  ColumnNamesType,
  ExplorerItemType,
} from '@/constants/table-columns-config';

// Helper type guard to check if a column has accessorKey
function hasAccessorKey(
  column: ColumnDef<NormalizedAgreement>,
): column is ColumnDef<NormalizedAgreement> & { accessorKey: string } {
  return 'accessorKey' in column;
}

// Format date string to display date only (without time)
function formatDateWithoutTime(dateString: string | undefined): string {
  if (!dateString) return '';
  try {
    return moment(dateString).format('DD.MM.YYYY');
  } catch (error) {
    return dateString;
  }
}

// Check if a field is a date field based on its name or value
function isDateField(key: string, value: any): boolean {
  // Check for known date field names
  const dateFieldNames = ['created', 'modified', 'filmed_date', 'upload_date'];
  if (dateFieldNames.includes(key)) return true;

  // Check if the value is a date string format
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/))
    return true;

  return false;
}

export const handleExportExcel = async (
  columns: ColumnDef<NormalizedAgreement>[],
  options?: {
    /** If provided, these rows will be exported instead of the table's current page rows */
    dataOverride?: NormalizedAgreement[];
    /** Optional override for the output file name */
    fileName?: string;
  },
) => {
  const steps = useGlobalStore.getState().steps;
  const directories = useApiManagerStore.getState().directories;
  const getInsuranceObjects = useApiManagerStore.getState().getInsuranceObjects;
  const getAutoFetchUsers = useGlobalStore.getState().getAutoFetchUsers;
  const indexes = useGlobalStore.getState().indexes;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Data');

  let agreements: AgreementDTO[] = [];

  const agreementsIds = await useSearchStore
    .getState()
    .searchAgreementUuidsForMyDocuments('dashboard', true);
  if (agreementsIds.length > 50) {
    // break array into chunks of 100
    const chunks = [];
    for (let i = 0; i < agreementsIds.length; i += 100) {
      chunks.push(agreementsIds.slice(i, i + 100));
    }

    const responses = await Promise.all(
      chunks.map(async (chunk) => {
        return useApiManagerStore.getState().getAgreements(chunk);
      }),
    );
    agreements = responses.flat();
  } else {
    agreements = await useApiManagerStore
      .getState()
      .getAgreements(agreementsIds);
  }

  const uniqueInsuranceObjectIds = Array.from(
    new Set(
      agreements
        .map((agreement) => agreement.insurance_object)
        .filter((id): id is string => Boolean(id)),
    ),
  );

  const insuranceObjectsFromAgreements =
    uniqueInsuranceObjectIds.length > 0
      ? await getInsuranceObjects(uniqueInsuranceObjectIds)
      : [];

  const directoryIdByInsuranceObject = new Map(
    insuranceObjectsFromAgreements.map((entry) => [
      entry.uuid,
      entry.directory,
    ]),
  );

  const directoryById = new Map(
    directories.map((directory) => [directory.uuid, directory.abbreviation]),
  );

  const usersFromAgreements = await getAutoFetchUsers([
    ...new Set(
      agreements
        .flatMap((agreement) => [agreement.assignee, agreement.creator])
        .filter(Boolean) as string[],
    ),
  ]);

  // Add headers
  const headers = columns
    .filter(hasAccessorKey)
    .map(
      (column) =>
        COLUMN_NAMES(ExplorerItemType.AGREEMENT)[
          column.accessorKey as ColumnNamesType
        ] || column.accessorKey,
    );

  worksheet.addRow(headers);

  // Add data
  const filteredData = agreements.map((row) => {
    return columns.filter(hasAccessorKey).map((column) => {
      const key = column.accessorKey as keyof AgreementDTO;
      if (key === 'step') {
        return steps.find((step) => step.uuid === row[key])?.status_name;
      }
      if (key === 'insurance_object') {
        const directoryId = directoryIdByInsuranceObject.get(
          row[key] as string,
        );
        return directoryId ? (directoryById.get(directoryId) ?? '') : '';
      }

      if (key === 'index') {
        return indexes.find(
          (index) => !index.is_deleted && index.uuid === row[key],
        )?.name;
      }

      if (key === 'assignee' || key === 'creator') {
        return (
          usersFromAgreements.find((user) => user.uuid === row[key])
            ?.first_name +
          ' ' +
          usersFromAgreements.find((user) => user.uuid === row[key])?.last_name
        );
      }

      const value = row[key];

      // Format date fields
      if (isDateField(key as string, value)) {
        return formatDateWithoutTime(value as string);
      }

      return value;
    });
  });

  filteredData.forEach((row) => {
    worksheet.addRow(row);
  });

  // Generate blob and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options?.fileName ?? 'exported_data.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
